import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Trees,
  Clock,
  Compass,
  Binoculars,
  Shield,
  Map,
  Sun,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const Safari = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const safariHighlights = [
    {
      icon: <Trees size={32} />,
      title: 'Pilibhit Tiger Reserve',
      description:
        'Part of the Terai Arc Landscape, Pilibhit is known for dense sal forests, grasslands, and one of the highest tiger densities in North India.',
    },
    {
      icon: <Compass size={32} />,
      title: 'Open Gypsy Safaris',
      description:
        'Explore the core zones in government-approved open gypsy vehicles designed for silent, low-impact wildlife tracking.',
    },
    {
      icon: <Binoculars size={32} />,
      title: 'Expert Trackers',
      description:
        'Local forest guides with decades of experience reading pugmarks, alarm calls, and movement patterns.',
    },
    {
      icon: <Shield size={32} />,
      title: 'Ethical & Regulated',
      description:
        'Strictly regulated safaris ensuring animal safety, limited vehicles, and sustainable tourism practices.',
    },
    {
      icon: <Map size={32} />,
      title: 'Rich Biodiversity',
      description:
        'Besides tigers, spot leopards, swamp deer, hog deer, Bengal florican, and over 300 bird species.',
    },
    {
      icon: <Sun size={32} />,
      title: 'Golden Light Zones',
      description:
        'Morning and evening safaris offer extraordinary lighting conditions for photography and sightings.',
    },
  ];

  const safariTimings = [
    {
      title: 'Morning Safari',
      time: '6:00 AM – 10:00 AM',
      description:
        'Best for tracking predators returning from night hunts. Cool air, active wildlife.',
    },
    {
      title: 'Afternoon Safari',
      time: '2:30 PM – 6:00 PM',
      description:
        'Golden-hour sightings, grazing herbivores, and dramatic forest light.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <Navbar scrolled={scrolled} />

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1549366021-9f761d450615?q=85"
          alt="Safari Jeep"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center text-white px-4 max-w-4xl"
        >
          <p className="overline text-white/80 mb-4">WILDLIFE EXPERIENCE</p>
          <h1
            className="text-5xl md:text-7xl font-bold mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Jungle Safari
          </h1>
          <p className="text-xl md:text-2xl opacity-90 font-light">
            Into the tiger’s realm, guided by silence
          </p>
        </motion.div>
      </section>

      {/* Why Pilibhit */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2
              className="text-3xl md:text-4xl font-bold mb-8"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Why Pilibhit is Special
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: '#64748B' }}>
              Unlike crowded reserves, Pilibhit offers raw, immersive safaris. 
              Dense forests, tall grasslands, and river corridors create ideal 
              tiger territory — making sightings authentic, not manufactured.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: '#64748B' }}>
              This is a reserve for those who respect wildlife, patience, and 
              the quiet thrill of tracking nature on its own terms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Safari Highlights */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {safariHighlights.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl"
                style={{ background: '#F9F8F6' }}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ background: 'white', color: '#1A3C34' }}
                >
                  {item.icon}
                </div>
                <h3
                  className="text-xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
                >
                  {item.title}
                </h3>
                <p style={{ color: '#64748B' }}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Safari Timings */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Clock className="mx-auto mb-6" size={40} style={{ color: '#8F9E8B' }} />
            <h2
              className="text-4xl font-bold"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Safari Timings
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {safariTimings.map((slot, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-sm"
              >
                <h3
                  className="text-2xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
                >
                  {slot.title}
                </h3>
                <p className="mb-2 font-medium" style={{ color: '#8F9E8B' }}>
                  {slot.time}
                </p>
                <p style={{ color: '#64748B' }}>{slot.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why BaghHome */}
      <section className="section-padding" style={{ background: '#1A3C34' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Users className="mx-auto mb-6" size={40} style={{ color: '#8F9E8B' }} />
            <h2
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Why Safari with The Bagh Home
            </h2>
            <p className="text-lg mb-6 text-white/80">
              We are located strategically close to safari entry points, ensuring 
              early access without rushed travel. Our team coordinates permits, 
              timings, and trusted guides — so your experience stays seamless.
            </p>
            <p className="text-lg text-white/80">
              No commissions. No tourist traps. Just honest, well-planned safaris 
              rooted in respect for the forest.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Plan Your Safari Stay
            </h2>
            <p className="text-lg mb-8" style={{ color: '#64748B' }}>
              Combine comfort, conservation, and unforgettable wildlife moments.
            </p>
            <Link to="/rooms">
              <button className="btn-primary">View Rooms</button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Safari;

